import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Field, FormError, Input } from "@/components/ui/field";
import { AuthLayout } from "@/features/auth/auth-layout";
import { FormatPicker } from "@/features/configs/format-picker";
import { Pass } from "@/features/configs/pass";
import { FORMAT_INFO } from "@/features/configs/platforms";
import { checkInvite, registrationQuery, sessionQuery, useRegister } from "@/lib/api/queries";
import { applyApiError } from "@/lib/forms";

export const Route = createFileRoute("/register")({
  validateSearch: z.object({ code: z.string().optional() }),
  beforeLoad: async ({ context }) => {
    if (await context.queryClient.ensureQueryData(sessionQuery)) throw redirect({ to: "/" });
    await context.queryClient.ensureQueryData(registrationQuery);
  },
  component: RegisterPage,
});

const INVITE = /^[A-Za-z0-9]{4}-?[A-Za-z0-9]{4}$/;

const makeSchema = (inviteRequired: boolean) =>
  z.object({
    invite_code: inviteRequired
      ? z.string().trim().regex(INVITE, "Код имеет вид XXXX-XXXX")
      : z.string().trim(),
    username: z
      .string()
      .trim()
      .regex(
        /^[a-zA-Z0-9_.-]{3,32}$/,
        "3–32 символа: латиница, цифры, точка, дефис, подчёркивание",
      ),
    password: z.string().min(8, "Минимум 8 символов").max(128),
    config_format: z.enum(["amneziavpn", "amneziawg"]),
  });
type Values = z.infer<ReturnType<typeof makeSchema>>;

type InviteState = "unknown" | "valid" | "invalid";

function RegisterPage() {
  const { code } = Route.useSearch();
  const navigate = useNavigate();
  const register = useRegister();
  const [formError, setFormError] = useState<string>();
  const [invite, setInvite] = useState<InviteState>("unknown");
  const { data: registration } = useSuspenseQuery(registrationQuery);
  const inviteRequired = registration.invite_required;
  const schema = useMemo(() => makeSchema(inviteRequired), [inviteRequired]);
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { invite_code: code ?? "", config_format: "amneziawg" },
  });
  const { errors } = form.formState;
  const [username, format, inviteCode] = useWatch({
    control: form.control,
    name: ["username", "config_format", "invite_code"],
  });

  // Tell early whether the invite works, before the person types a password.
  useEffect(() => {
    const trimmed = inviteCode?.trim() ?? "";
    if (!inviteRequired || !INVITE.test(trimmed)) {
      setInvite("unknown");
      return;
    }
    const timer = setTimeout(() => {
      checkInvite(trimmed)
        .then((valid) => setInvite(valid ? "valid" : "invalid"))
        .catch(() => setInvite("unknown"));
    }, 350);
    return () => clearTimeout(timer);
  }, [inviteCode, inviteRequired]);

  const submit = form.handleSubmit(async (values) => {
    setFormError(undefined);
    try {
      await register.mutateAsync(values);
      await navigate({ to: "/", search: { welcome: true } });
    } catch (error) {
      setFormError(applyApiError(error, form.setError, ["username", "password", "invite_code"]));
    }
  });

  const inviteHint = invite === "valid" ? "Приглашение действует" : undefined;
  const inviteError =
    errors.invite_code?.message ??
    (invite === "invalid" ? "Код недействителен, истёк или уже использован" : undefined);

  return (
    <AuthLayout
      title="Регистрация"
      lead={
        inviteRequired
          ? "По приглашению. Первый конфиг выдадим сразу после регистрации."
          : "Первый конфиг выдадим сразу после регистрации."
      }
      aside={
        <Pass
          number="1"
          name={username?.trim() || "Ваш логин"}
          details={FORMAT_INFO[format].title}
          status={{ tone: "idle", label: "Ещё не подключался" }}
          stubLabel="QR"
        />
      }
    >
      <form onSubmit={submit} className="grid gap-4" noValidate>
        {inviteRequired && (
          <Field label="Код приглашения" error={inviteError} hint={inviteHint}>
            {(control) => (
              <Input
                {...control}
                {...form.register("invite_code")}
                placeholder="XXXX-XXXX"
                autoCapitalize="characters"
                autoComplete="off"
                spellCheck={false}
              />
            )}
          </Field>
        )}
        <Field label="Логин" error={errors.username?.message}>
          {(control) => (
            <Input
              {...control}
              {...form.register("username")}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
            />
          )}
        </Field>
        <Field label="Пароль" error={errors.password?.message} hint="Минимум 8 символов">
          {(control) => (
            <Input
              {...control}
              {...form.register("password")}
              type="password"
              autoComplete="new-password"
            />
          )}
        </Field>
        <Controller
          control={form.control}
          name="config_format"
          render={({ field }) => <FormatPicker value={field.value} onChange={field.onChange} />}
        />
        <FormError message={formError} />
        <Button type="submit" pending={register.isPending} className="mt-2">
          Зарегистрироваться
        </Button>
      </form>
      <p className="mt-8 text-sm text-muted">
        Уже есть аккаунт?{" "}
        <Link to="/login" className="text-amber-text underline-offset-4 hover:underline">
          Войти
        </Link>
      </p>
    </AuthLayout>
  );
}
